/**
 * Creature Discovery - Choose your fantasy creature
 * Ages 4-6, Large touch targets, Animated cards
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CreatureType } from './types';
import { CREATURE_CARDS } from './data';

// Shared design tokens with the rest of the app's games.
const PASTEL_COLORS = [
  '#dbeef5',
  '#fce8d2',
  '#dcecda',
];
const INK_GREEN = '#2a6d5b';
const BRAND_GREEN = '#42a57a';
const SERIF = "'Fraunces', Georgia, serif";
const SANS = "'Inter', system-ui, -apple-system, sans-serif";

function getRandomCreatures(count: number) {
  const shuffled = [...CREATURE_CARDS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

interface CreatureDiscoveryProps {
  language: 'en' | 'es';
  onSelectCreature: (creature: CreatureType) => void;
}

const TEXT = {
  en: {
    title: 'Choose Your Creature Friend',
    subtitle: 'Which magical creature do you want to give superpowers?',
    tapToChoose: 'Tap to choose',
  },
  es: {
    title: 'Elige tu Amigo Criatura',
    subtitle: '¿A qué criatura mágica quieres darle superpoderes?',
    tapToChoose: 'Toca para elegir',
  },
};

export default function CreatureDiscovery({ language, onSelectCreature }: CreatureDiscoveryProps) {
  const t = TEXT[language];
  const randomCreatures = useMemo(() => getRandomCreatures(3), []);

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center p-6">
      <div className="max-w-6xl w-full">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-8"
        >
          <h2
            className="text-5xl md:text-7xl mb-3"
            style={{
              fontFamily: SERIF,
              fontWeight: 300,
              color: INK_GREEN,
              letterSpacing: '-0.015em',
            }}
          >
            {t.title}
          </h2>
          <p
            className="text-2xl md:text-3xl"
            style={{ fontFamily: SANS, fontWeight: 400, color: '#355c46' }}
          >
            {t.subtitle}
          </p>
        </motion.div>

        {/* Creature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {randomCreatures.map((creature, index) => {
            const pastel = PASTEL_COLORS[index % PASTEL_COLORS.length];
            return (
              <motion.button
                key={creature.type}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  delay: index * 0.12,
                  type: 'spring',
                  stiffness: 260,
                  damping: 22,
                }}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onSelectCreature(creature.type)}
                className="text-center cursor-pointer flex flex-col"
                style={{
                  background: '#ffffff',
                  border: '1px solid #e8e0c8',
                  borderRadius: 18,
                  padding: 14,
                  transition: 'transform 200ms ease, box-shadow 200ms ease',
                }}
              >
                {/* Pastel inner tile holds the creature emoji */}
                <div
                  style={{
                    background: pastel,
                    borderRadius: 14,
                    padding: '36px 16px 28px',
                    textAlign: 'center',
                  }}
                >
                  <motion.div
                    animate={{
                      y: [0, -7, 0],
                      rotate: [0, 4, -4, 0],
                    }}
                    transition={{
                      duration: 2.4,
                      repeat: Infinity,
                      repeatType: 'reverse',
                      ease: 'easeInOut',
                      delay: index * 0.3,
                    }}
                    style={{ fontSize: 96, lineHeight: 1 }}
                  >
                    {creature.emoji}
                  </motion.div>
                </div>

                {/* Creature name — italic Fraunces, ink green */}
                <h3
                  style={{
                    fontFamily: SERIF,
                    fontStyle: 'italic',
                    fontSize: 30,
                    fontWeight: 600,
                    color: INK_GREEN,
                    marginTop: 16,
                    marginBottom: 8,
                  }}
                >
                  {creature.name[language]}
                </h3>

                <p
                  style={{
                    fontFamily: SANS,
                    fontSize: 16,
                    fontWeight: 400,
                    color: '#5f6e66',
                    marginBottom: 16,
                    flexGrow: 1,
                  }}
                >
                  {creature.description[language]}
                </p>

                {/* Brand-green CTA */}
                <div
                  style={{
                    background: BRAND_GREEN,
                    color: '#ffffff',
                    fontFamily: SANS,
                    fontSize: 16,
                    fontWeight: 600,
                    padding: '10px 18px',
                    borderRadius: 999,
                    alignSelf: 'center',
                  }}
                >
                  {t.tapToChoose}
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Accessibility note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-8"
          style={{ fontFamily: SANS, fontSize: 14, color: '#9a9a9a' }}
        >
          Designed with accessibility in mind. We welcome your feedback on vision, sound, and interaction enhancements.
        </motion.div>
      </div>
    </div>
  );
}
